import { Suspense, useEffect, useState } from 'react';

import CssBaseline from '@mui/material/CssBaseline';
import { CircularLoading } from 'components/CircularLoading/CircularLoading';
import { getBrowserLocale } from 'helpers/helpers';
import { createHandlers } from 'mocks/handlers';
import { Outlet } from 'react-router-dom';
import { AdminService } from 'services/admin/admin';
import ApiClient from 'services/axios-config';
import PendoClient from 'services/pendo/Client';
import { fetchOrganization } from 'store/company/CompanySlice';
import { fetchConsents } from 'store/consents/ConsentsSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectAuth, selectCompany, selectUser } from 'store/store';
import { fetchUser } from 'store/user/UserSlice';

const PENDO_ANONYMOUS_ACCOUNT_ID = 'ANONYMOUS_ACCOUNT';

export default function App() {
  const dispatch = useAppDispatch();
  const {
    isAuthenticated, userId,
    accessToken, refreshToken,
  } = useAppSelector(selectAuth);
  const {
    organization,
  } = useAppSelector(selectCompany);
  const [initialized, setInitialized] = useState(false);

  const {
    user,
  } = useAppSelector(selectUser);

  const identifyPendo = async (pendoUser: User, organizationId?: number, organizationName?: string) => {
    let accountId: number | undefined;
    try {
      if (organizationId !== undefined) {
        try {
          accountId = await AdminService.Company.GetAccountOrganization(organizationId);
        } catch (error) {
          console.warn('Failed to fetch account organization, falling back to organizationId for Pendo account id', error);
        }
      }

      const resolvedAccountId = accountId ?? organizationId;
      const identifyAccountId = resolvedAccountId !== undefined
        ? `${resolvedAccountId}`
        : PENDO_ANONYMOUS_ACCOUNT_ID;

      const browserLanguage = getBrowserLocale();

      PendoClient.identify({
        companyInfo: {
          accountId: identifyAccountId,
          organizationName: organizationName || undefined,
        },
        visitorInfo: {
          id: `${pendoUser.userId}`,
          role: pendoUser.role,
          language: browserLanguage,
        },
      });
    } catch (error) {
      console.error('Failed to identify Pendo user', error);
    }
  };

  useEffect(() => {
    PendoClient.init();
  }, []);

  useEffect(() => {
    if (!user) {
      return;
    }

    identifyPendo(user, organization?.organizationId, organization?.organizationName);
  }, [organization, user]);

  useEffect(() => {
    let active = true;
    setInitialized(false);
    // eslint-disable-next-line global-require
    if (process.env.NODE_ENV === 'development') {
      const { worker } = require('./mocks/browser');
      worker.use(...(createHandlers(window.env.PUBLIC_API_BASE_URL)));
      worker.start();
    }

    if (isAuthenticated && userId) {
      ApiClient.setAuthorizationHeader(accessToken, refreshToken);
      const promises: Promise<any>[] = [];

      promises.push(dispatch(fetchConsents(userId)));
      promises.push(dispatch(fetchUser(userId)));
      promises.push(dispatch(fetchOrganization(userId)));
      Promise.all(promises)
        .finally(async () => {
          if (active) setInitialized(true);
        });
    } else {
      setInitialized(true);
    }

    return () => { active = false; };
  }, [isAuthenticated, userId, accessToken, refreshToken, window.env.PUBLIC_API_BASE_URL]);

  return (
    <>
      <CssBaseline />

      <Suspense fallback={<CircularLoading />}>
        {initialized && <Outlet />}
      </Suspense>
    </>
  );
}
